/* spotlight.ts — geometry for the coachmark overlay.

   No positioning library in this app (checked: no floating-ui / popper / radix),
   and we don't need one: we tag our own anchors and each step's onEnter guarantees
   the target is on-screen, so a preferred placement + a viewport clamp is enough. */

import type { Placement } from './types';
import { inApp } from '../../theme/uiScale';

export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Locate a step's target by its `data-tutorial` attribute. */
export function measureAnchor(anchor: string): AnchorRect | null {
  const el = document.querySelector<HTMLElement>(`[data-tutorial="${anchor}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null; // present but not rendered
  // In the app's own (zoomed) units, like the bubble's offsetWidth and the overlay's left/top.
  return { top: inApp(r.top), left: inApp(r.left), width: inApp(r.width), height: inApp(r.height) };
}

/** Bring an anchor into view before spotlighting it. */
export function scrollAnchorIntoView(anchor: string): void {
  const el = document.querySelector<HTMLElement>(`[data-tutorial="${anchor}"]`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

export interface BubblePos {
  top: number;
  left: number;
  /** Which side the bubble ended up on (after collision fallback) — for the arrow. */
  side: Placement;
}

const GAP = 16; // px between spotlight edge and bubble
const MARGIN = 12; // min distance from viewport edge

/**
 * Position a bubble of (w, h) around the anchor at the preferred placement,
 * flipping to the opposite side if it would overflow and clamping into view.
 */
export function placeBubble(
  rect: AnchorRect,
  preferred: Placement,
  w: number,
  h: number,
): BubblePos {
  const vw = inApp(window.innerWidth);
  const vh = inApp(window.innerHeight);

  const fits = (p: Placement): boolean => {
    switch (p) {
      case 'top': return rect.top - GAP - h >= MARGIN;
      case 'bottom': return rect.top + rect.height + GAP + h <= vh - MARGIN;
      case 'left': return rect.left - GAP - w >= MARGIN;
      case 'right': return rect.left + rect.width + GAP + w <= vw - MARGIN;
    }
  };
  const opposite: Record<Placement, Placement> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const side: Placement = fits(preferred) ? preferred : (fits(opposite[preferred]) ? opposite[preferred] : preferred);

  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    left = rect.left + rect.width / 2 - w / 2;
    top = side === 'top' ? rect.top - GAP - h : rect.top + rect.height + GAP;
  } else {
    top = rect.top + rect.height / 2 - h / 2;
    left = side === 'left' ? rect.left - GAP - w : rect.left + rect.width + GAP;
  }

  // Clamp into the viewport so the bubble is never cut off.
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
  return { top, left, side };
}
